/**
 * Session Detail Component for Enhanced Teacher Dashboard
 * Shows detailed session information with multiple tabs
 */

import React, { useState, useEffect } from 'react';
import { SessionDetail, Transcript, PronunciationAssessment, QuizResult, RoleplayLog, SafetyAlert, SessionPronunciationAnalytics } from './types';
import PronunciationResults from './PronunciationResults';
import { pronunciationService } from '../../services/pronunciationService';
import { summaryService } from '../../services/summaryService';
import { getApiUrl } from '../../utils/apiUtils';
import { CheckCircleIcon, MessageIcon, AlertIcon, CloseIcon } from '../SvgConsolidated/SvgIcons';

interface SessionDetailProps {
  sessionId: string;
  storySessionId?: string | null;
  onClose: () => void;
}

export default function SessionDetailComponent({ sessionId, storySessionId, onClose }: SessionDetailProps) {
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [transcript, setTranscript] = useState<Transcript[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pronunciationData, setPronunciationData] = useState<PronunciationAssessment[]>([]);
  const [pronunciationAnalytics, setPronunciationAnalytics] = useState<SessionPronunciationAnalytics | null>(null);
  const [quizResults, setQuizResults] = useState<QuizResult[]>([]);
  const [roleplayLogs, setRoleplayLogs] = useState<RoleplayLog[]>([]);
  const [safetyAlerts, setSafetyAlerts] = useState<SafetyAlert[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'transcript' | 'pronunciation' | 'quiz' | 'roleplay' | 'safety' | 'summary'>('overview');
  const [summaryData, setSummaryData] = useState<any>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSessionDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadSessionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load session details, optionally filtered by story_session_id
      const baseUrl = `/v1/dashboard/session/${sessionId}`;
      const url = storySessionId ? `${baseUrl}?story_session_id=${storySessionId}` : baseUrl;
      const sessionResponse = await fetch(getApiUrl(url));
      if (!sessionResponse.ok) {
        throw new Error('Failed to load session details');
      }
      const sessionData = await sessionResponse.json();
      setSession(sessionData);

      // Load related data based on agent type
      await Promise.all([
        loadTranscripts(),
        loadPronunciationData(),
        loadQuizResults(),
        loadRoleplayLogs(),
        loadSafetyAlerts()
      ]);

    } catch (error) {
      console.error('Error loading session details:', error);
      setError('Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  const loadTranscripts = async () => {
    try {
      const baseUrl = `/v1/dashboard/session/${sessionId}/transcripts`;
      const url = storySessionId ? `${baseUrl}?story_session_id=${storySessionId}` : baseUrl;
      const response = await fetch(getApiUrl(url));
      if (response.ok) {
        const data = await response.json();
        setTranscript(data);
      }
    } catch (error) {
      console.error('Error loading transcripts:', error);
    }
  };

  const loadPronunciationData = async () => {
    try {
      // Build URL with optional story_session_id parameter for filtering
      const baseUrl = `/v1/dashboard/session/${sessionId}/pronunciation`;
      const url = storySessionId ? `${baseUrl}?story_session_id=${storySessionId}` : baseUrl;
      
      // Fetch comprehensive pronunciation analytics
      const analytics = await pronunciationService.getSessionAnalytics(sessionId, storySessionId);
      setPronunciationAnalytics(analytics);
      
      // Also fetch old format for legacy chart if needed
      const response = await fetch(getApiUrl(url));
      if (response.ok) {
        const data = await response.json();
        setPronunciationData(data.assessments || []);
      }
    } catch (error) {
      console.error('Error loading pronunciation data:', error);
    }
  };

  const loadQuizResults = async () => {
    try {
      const baseUrl = `/v1/dashboard/session/${sessionId}/quiz`;
      const url = storySessionId ? `${baseUrl}?story_session_id=${storySessionId}` : baseUrl;
      const response = await fetch(getApiUrl(url));
      if (response.ok) {
        const data = await response.json();
        setQuizResults(data);
      }
    } catch (error) {
      console.error('Error loading quiz results:', error);
    }
  };

  const loadRoleplayLogs = async () => {
    try {
      const baseUrl = `/v1/dashboard/session/${sessionId}/roleplay`;
      const url = storySessionId ? `${baseUrl}?story_session_id=${storySessionId}` : baseUrl;
      const response = await fetch(getApiUrl(url));
      if (response.ok) {
        const data = await response.json();
        setRoleplayLogs(data);
      }
    } catch (error) {
      console.error('Error loading roleplay logs:', error);
    }
  };

  const loadSafetyAlerts = async () => {
    try {
      const baseUrl = `/v1/dashboard/session/${sessionId}/safety`;
      const url = storySessionId ? `${baseUrl}?story_session_id=${storySessionId}` : baseUrl;
      const response = await fetch(getApiUrl(url));
      if (response.ok) {
        const data = await response.json();
        setSafetyAlerts(data);
      }
    } catch (error) {
      console.error('Error loading safety alerts:', error);
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getAgentIcon = (agentType: string) => {
    switch (agentType) {
      case 'wordwizard': return '🧙‍♂️';
      case 'quizcritter': return '🦎';
      case 'roleranger': return '🤠';
      case 'evidencebuddy': return '🔍';
      case 'safetyshield': return '🛡️';
      default: return '🤖';
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Session Overview Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-blue-600 text-sm font-medium">Duration</div>
          <div className="text-2xl font-semibold text-blue-900">
            {formatDuration(session?.duration_seconds || 0)}
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-green-600 text-sm font-medium">Completion</div>
          <div className="text-2xl font-semibold text-green-900">
            {session?.completionRate || 0}%
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-purple-600 text-sm font-medium">Engagement</div>
          <div className="text-2xl font-semibold text-purple-900">
            {session?.engagementScore || 0}%
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="text-yellow-600 text-sm font-medium">Interventions</div>
          <div className="text-2xl font-semibold text-yellow-900">
            {session?.interventionsCount || 0}
          </div>
        </div>
      </div>

      {/* Performance Breakdown */}
      {session?.agentType === 'wordwizard' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Word Wizard Performance</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-600">Total Words</div>
              <div className="text-xl font-semibold">{session?.totalWords || 0}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Correct</div>
              <div className="text-xl font-semibold text-green-600">{session?.correctWords || 0}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Average Accuracy</div>
              <div className="text-xl font-semibold text-blue-600">{session?.averageAccuracy || 0}%</div>
            </div>
          </div>
        </div>
      )}

      {session?.agentType === 'quizcritter' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Quiz Critter Performance</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-600">Total Questions</div>
              <div className="text-xl font-semibold">{session?.totalQuizQuestions || 0}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Correct Answers</div>
              <div className="text-xl font-semibold text-green-600">{session?.correctAnswers || 0}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Quiz Score</div>
              <div className="text-xl font-semibold text-blue-600">
                {session?.totalQuizQuestions ? Math.round((session?.correctAnswers || 0) / session.totalQuizQuestions * 100) : 0}%
              </div>
            </div>
          </div>
        </div>
      )}

      {session?.agentType === 'roleranger' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Role Ranger Performance</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600">Roleplay Turns</div>
              <div className="text-xl font-semibold">{session?.roleplayTurns || 0}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Average Emotion Score</div>
              <div className="text-xl font-semibold text-purple-600">
                {roleplayLogs.length ? 
                  Math.round(roleplayLogs.reduce((sum, log) => sum + log.emotionScore, 0) / roleplayLogs.length) : 0}%
              </div>
            </div>
          </div>
        </div>
      )}

      {safetyAlerts.length > 0 && (
        <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-400">
          <h3 className="text-red-800 font-medium">Safety Alerts ({safetyAlerts.length})</h3>
          <div className="mt-2 space-y-1">
            {safetyAlerts.slice(0, 3).map(alert => (
              <div key={alert.id} className="text-sm text-red-700">
                • {alert.description} ({alert.severity})
              </div>
            ))}
            {safetyAlerts.length > 3 && (
              <div className="text-sm text-red-600">+ {safetyAlerts.length - 3} more alerts</div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderQuizTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800">Quiz Results</h3>
      {quizResults.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No quiz data available for this session
        </div>
      ) : (
        <div className="space-y-3">
          {quizResults.map((result, index) => (
            <div key={result.id} className={`p-4 rounded-lg border ${result.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-medium text-gray-800">Question {index + 1}</div>
                  <div className="text-sm text-gray-600 mt-1">{result.questionText}</div>
                  <div className="mt-2 grid md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Correct Answer:</span>
                      <span className="ml-1 text-green-700 font-medium">{result.correctAnswer}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Student Answer:</span>
                      <span className={`ml-1 font-medium ${result.isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                        {result.studentAnswer}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="ml-4 text-right">
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    result.isCorrect 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {result.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {result.responseTime}s
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderRoleplayTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800">Roleplay Session</h3>
      {roleplayLogs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No roleplay data available for this session
        </div>
      ) : (
        <div className="space-y-3">
          {roleplayLogs.map(log => (
            <div key={log.id} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-medium text-purple-800">
                    Turn {log.turn}: {log.character}
                  </div>
                  <div className="text-gray-700 mt-1">{log.dialogue}</div>
                </div>
                <div className="ml-4 text-right">
                  <div className="text-xs text-purple-600 font-medium">
                    Emotion: {log.emotionScore}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const loadSummary = async () => {
    if (!storySessionId) {
      return;
    }

    setLoadingSummary(true);
    try {
      const summary = await summaryService.getSummary(storySessionId);
      setSummaryData(summary);
    } catch (err: any) {
      console.error('Error loading summary:', err);
      // Summary might not exist yet
      setSummaryData(null);
    } finally {
      setLoadingSummary(false);
    }
  };

  // Load summary when tab is activated
  useEffect(() => {
    if (activeTab === 'summary' && !summaryData && !loadingSummary && storySessionId) {
      loadSummary();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, storySessionId]);

  const renderSummaryTab = () => {

    if (!storySessionId) {
      return (
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">📋</span>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Story Session</h3>
          <p className="text-gray-600">
            This session is not associated with a story reading activity.
          </p>
        </div>
      );
    }

    if (loadingSummary) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading summary...</span>
        </div>
      );
    }

    if (!summaryData) {
      return (
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">📝</span>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Summary Not Generated</h3>
          <p className="text-gray-600 mb-4">
            The reading summary has not been generated for this session yet.
          </p>
          <p className="text-sm text-gray-500">
            Summaries are generated when students complete a story and click "Generate Summary".
          </p>
        </div>
      );
    }

    const teacher = summaryData.teacher_summary;
    const rubric = teacher?.rubric_assessment;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">📋 Reading Summary</h3>
          <div className="flex gap-2">
            <button
              onClick={() => summaryService.downloadSummary(storySessionId!, 'txt', 'teacher')}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              📄 Download TXT
            </button>
            <button
              onClick={() => summaryService.downloadSummary(storySessionId!, 'pdf', 'teacher')}
              className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
            >
              📑 Download PDF
            </button>
          </div>
        </div>

        {/* Rubric Assessment */}
        {rubric && (
          <div className="bg-white border-2 border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-3">Fluency Rubric Assessment</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium text-gray-700">Rate (WCPM)</span>
                    <span className={`px-2 py-1 rounded text-sm font-bold ${
                      summaryService.getRubricScoreColor(rubric.rate.score, 3)
                    }`}>
                      {rubric.rate.score}/3
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{rubric.rate.wcpm} words per minute</p>
                  <p className="text-xs text-gray-500 mt-1">{rubric.rate.description}</p>
                </div>

                <div className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium text-gray-700">Accuracy</span>
                    <span className={`px-2 py-1 rounded text-sm font-bold ${
                      summaryService.getRubricScoreColor(rubric.accuracy.score, 3)
                    }`}>
                      {rubric.accuracy.score}/3
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{rubric.accuracy.accuracy_percent}% accurate</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {rubric.accuracy.words_correct} of {rubric.accuracy.total_words} words correct
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium text-gray-700">Prosody</span>
                    <span className={`px-2 py-1 rounded text-sm font-bold ${
                      summaryService.getRubricScoreColor(rubric.prosody.score, 4)
                    }`}>
                      {rubric.prosody.score}/4
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Expression: {rubric.prosody.expression_quality}
                  </p>
                  <p className="text-xs text-gray-600">
                    Phrasing: {rubric.prosody.phrasing_quality}
                  </p>
                </div>

                <div className="p-3 bg-purple-100 border-2 border-purple-300 rounded">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-purple-900">Overall Score</span>
                    <span className="text-2xl font-bold text-purple-900">
                      {rubric.overall_score}/10
                    </span>
                  </div>
                  <p className="text-sm text-purple-700 mt-1">{rubric.overall_assessment}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Overview */}
        {teacher?.student_performance_overview && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Performance Overview</h4>
            <p className="text-sm text-blue-800">{teacher.student_performance_overview}</p>
          </div>
        )}

        {/* Strengths */}
        {teacher?.strengths && teacher.strengths.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">💪 Strengths</h4>
            <ul className="space-y-1">
              {teacher.strengths.map((strength: string, idx: number) => (
                <li key={idx} className="text-sm text-green-800 flex items-start">
                  <span className="mr-2">•</span>
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Areas for Improvement */}
        {teacher?.areas_for_improvement && teacher.areas_for_improvement.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">📈 Areas for Growth</h4>
            <ul className="space-y-1">
              {teacher.areas_for_improvement.map((area: string, idx: number) => (
                <li key={idx} className="text-sm text-yellow-800 flex items-start">
                  <span className="mr-2">•</span>
                  <span>{area}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Instructional Recommendations */}
        {teacher?.instructional_recommendations && teacher.instructional_recommendations.length > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-indigo-900 mb-2">🎯 Recommendations</h4>
            <ul className="space-y-1">
              {teacher.instructional_recommendations.map((rec: string, idx: number) => (
                <li key={idx} className="text-sm text-indigo-800 flex items-start">
                  <span className="mr-2">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Next Steps */}
        {teacher?.next_steps && teacher.next_steps.length > 0 && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-2">🚀 Next Steps</h4>
            <ul className="space-y-1">
              {teacher.next_steps.map((step: string, idx: number) => (
                <li key={idx} className="text-sm text-purple-800 flex items-start">
                  <span className="mr-2">•</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderSafetyTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800">Safety Monitoring</h3>
      {safetyAlerts.length === 0 ? (
        <div className="text-center py-8 text-green-100 bg-green-50 rounded-lg border border-green-200">
          <div className="text-green-600 mb-2">
            <CheckCircleIcon className="w-12 h-12 mx-auto" />
          </div>
          <p className="text-green-800 font-medium">No safety alerts for this session</p>
          <p className="text-green-600 text-sm">All interactions were appropriate and safe</p>
        </div>
      ) : (
        <div className="space-y-3">
          {safetyAlerts.map(alert => (
            <div key={alert.id} className={`p-4 rounded-lg border ${
              alert.severity === 'critical' ? 'border-red-300 bg-red-50' :
              alert.severity === 'high' ? 'border-orange-300 bg-orange-50' :
              alert.severity === 'medium' ? 'border-yellow-300 bg-yellow-50' :
              'border-blue-300 bg-blue-50'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                      alert.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                      alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {alert.severity.toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-gray-800">
                      {alert.alertType.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="text-gray-700 mt-1">{alert.description}</div>
                  {alert.resolved && alert.resolverNotes && (
                    <div className="mt-2 p-2 bg-green-100 rounded text-sm text-green-800">
                      <strong>Resolved:</strong> {alert.resolverNotes}
                    </div>
                  )}
                </div>
                <div className="ml-4 text-right">
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    alert.resolved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {alert.resolved ? '✓ Resolved' : '⚠ Open'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {new Date(alert.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderTranscriptTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800">Session Transcript</h3>
      {transcript.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <div className="text-gray-400 mb-4">
            <MessageIcon className="w-16 h-16 mx-auto" />
          </div>
          <p>No transcript available for this session</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {transcript.map((transcriptItem: Transcript, index: number) => (
            <div
              key={transcriptItem.id || `transcript-${index}`}
              className={`flex space-x-3 ${
                transcriptItem.speaker === 'student' ? 'justify-end' : 'justify-start'
              }`}
            >
              {transcriptItem.speaker === 'agent' && (
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-sm">🤖</span>
                  </div>
                </div>
              )}
              
              <div
                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg border ${
                  transcriptItem.speaker === 'student'
                    ? 'bg-blue-50 border-blue-200 text-blue-800'
                    : 'bg-green-50 border-green-200 text-green-800'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium capitalize">
                    {transcriptItem.speaker}
                  </span>
                  <span className="text-xs text-gray-500">
                    #{transcriptItem.sequence}
                  </span>
                </div>
                <div className="text-sm">{transcriptItem.content}</div>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                  <span>{new Date(transcriptItem.timestamp).toLocaleTimeString()}</span>
                  {transcriptItem.confidence !== undefined && (
                    <span className="ml-2">
                      Confidence: {transcriptItem.confidence}%
                    </span>
                  )}
                </div>
              </div>

              {transcriptItem.speaker === 'student' && (
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm">👤</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading session details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-600 mb-4">
              <AlertIcon className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-gray-800 font-medium mb-2">Error Loading Session</p>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
                <span>{getAgentIcon(session?.agentType || '')}</span>
                <span>Session Details - {session?.storyTitle}</span>
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <CloseIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { key: 'overview', label: 'Overview', icon: '📊' },
              { key: 'transcript', label: 'Transcript', icon: '💬' },
              { key: 'pronunciation', label: 'Pronunciation', icon: '🗣️' },
              { key: 'quiz', label: 'Quiz Results', icon: '🧩' },
              { key: 'roleplay', label: 'Roleplay', icon: '🎭' },
              { key: 'summary', label: 'Reading Summary', icon: '📋' },
              { key: 'safety', label: 'Safety', icon: '🛡️' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'transcript' && renderTranscriptTab()}
          {activeTab === 'pronunciation' && (
            <div className="space-y-6">
              {pronunciationAnalytics ? (
                <PronunciationResults 
                  data={pronunciationAnalytics}
                  sessionId={sessionId}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Loading pronunciation data...
                </div>
              )}
            </div>
          )}
          {activeTab === 'quiz' && renderQuizTab()}
          {activeTab === 'roleplay' && renderRoleplayTab()}
          {activeTab === 'summary' && renderSummaryTab()}
          {activeTab === 'safety' && renderSafetyTab()}
        </div>
      </div>
    </div>
  );
}